import { onBeforeUnmount, onMounted } from "vue";

/**
 * Publishes the VISUAL viewport — the part of the page the user can actually see once the software
 * keyboard is up — as CSS custom properties on `<html>`.
 *
 * ⚠️ WHY THIS EXISTS AT ALL. Nothing in the app knew about the keyboard before this, and every
 * dropdown in the app is opened WITH the keyboard up. Vuetify's connected location strategy builds
 * its viewport box from `getScrollParents(contentEl)`
 * (`vuetify/lib/components/VOverlay/locationStrategies.js`); with no scrollable ancestor it falls
 * back to `document.documentElement`, i.e. the LAYOUT viewport. iOS Safari does not shrink the
 * layout viewport for the keyboard — only `visualViewport.height` changes — so Vuetify believed it
 * had ~844px of room when ~450px were visible and placed menus behind the keyboard. `100dvh` is the
 * same measurement and was no better. `#alkokh-overlay-target` consumes these vars (App.vue) to
 * become that missing scroll parent.
 *
 * ⚠️ `position: fixed` ANCHORS TO THE LAYOUT VIEWPORT TOO. That is why `--app-vv-top` exists
 * alongside the height: on iOS the visual viewport can be scrolled WITHIN the layout viewport
 * (`visualViewport.offsetTop`), and a layer pinned at `inset-block-start: 0` would then sit above
 * the visible area. The phone shell frame (`.mobile-shell`) is sized from both, and the bottom tab
 * bar leaves the frame while the keyboard is open — see `html.app-keyboard-open` below.
 *
 * ⚠️ THE FALLBACKS ARE TODAY'S GEOMETRY, EXACTLY. A browser with no `visualViewport` gets
 * `0px / 100dvh / 0 / 0px`, which is what every consumer already assumed, so nothing regresses.
 *
 * ⚠️ NOT VERIFIABLE IN DEVTOOLS. Device emulation does not simulate a software keyboard, so
 * `visualViewport` never resizes there. Changes here must be checked on real hardware, and on BOTH
 * platforms — Android Chrome resizes the layout viewport, iOS Safari does not, and that difference
 * is the entire subject.
 *
 * Published on `document.documentElement`:
 *   --app-vv-top         offset of the visible area inside the layout viewport
 *   --app-vv-height      height of the visible area
 *   --app-keyboard-inset space hidden at the bottom (keyboard, mostly)
 *   --app-keyboard-open  0 or 1 — a unitless flag CSS can multiply by, since CSS cannot branch
 */

/**
 * Below this the inset is not a keyboard. iOS reports small insets for the URL bar collapsing and
 * for the form-assistant accessory strip; treating those as "keyboard open" would flicker the tab
 * bar during an ordinary scroll.
 */
const KEYBOARD_MIN_INSET_PX = 120;

export function useVisualViewport() {
  let frame = 0;

  function publish() {
    frame = 0;

    const root = document.documentElement;
    const vv = window.visualViewport;

    if (!vv) {
      root.style.setProperty("--app-vv-top", "0px");
      root.style.setProperty("--app-vv-height", "100dvh");
      root.style.setProperty("--app-keyboard-inset", "0px");
      root.style.setProperty("--app-keyboard-open", "0");
      root.classList.remove("app-keyboard-open");

      return;
    }

    // `window.innerHeight` is the LAYOUT viewport; the difference is what the keyboard covers.
    const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);

    /*
     * ⚠️ OPEN/CLOSED IGNORES `offsetTop`. `inset` above is where the keyboard's top edge sits, which
     * the POS panels need. But while iOS pans the visible area down to a focused field, `offsetTop`
     * grows and `inset` shrinks under the threshold with the keyboard still up — the flag flipped to
     * "closed" and the tab bar slid back in mid-screen. Whether a keyboard is up is just how much
     * shorter the visible area is than the layout viewport.
     */
    const keyboardOpen = window.innerHeight - vv.height > KEYBOARD_MIN_INSET_PX;

    root.style.setProperty("--app-vv-top", `${Math.round(vv.offsetTop)}px`);
    root.style.setProperty("--app-vv-height", `${Math.round(vv.height)}px`);
    root.style.setProperty("--app-keyboard-inset", `${Math.round(inset)}px`);
    root.style.setProperty("--app-keyboard-open", keyboardOpen ? "1" : "0");

    // The same flag as a class, for rules that must switch a property CSS cannot multiply
    // (`display`). `mobile-shell.scss` uses it to take the tab bar out of the frame.
    root.classList.toggle("app-keyboard-open", keyboardOpen);
  }

  /**
   * `visualViewport` fires a burst of events per keyboard animation frame, and every consumer of
   * these vars triggers layout. One write per frame is enough.
   */
  function schedule() {
    if (frame)
      return;

    frame = requestAnimationFrame(publish);
  }

  onMounted(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    publish();

    // `scroll` matters as much as `resize`: on iOS the visual viewport moves within the layout
    // viewport without changing size, and a layer positioned from `--app-vv-top` must follow it.
    window.visualViewport?.addEventListener("resize", schedule);
    window.visualViewport?.addEventListener("scroll", schedule);

    // Orientation changes and desktop window resizes never reach `visualViewport` on some engines.
    window.addEventListener("resize", schedule);
  });

  onBeforeUnmount(() => {
    if (typeof window === "undefined")
      return;

    if (frame)
      cancelAnimationFrame(frame);

    window.visualViewport?.removeEventListener("resize", schedule);
    window.visualViewport?.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
  });
}
